import React, { useState, useEffect } from "react";
import axios from "axios";
import { Header } from "@/components/layout/Header";
import { 
    ShoppingBag, Package, Trash2, CreditCard, 
    Smartphone, Search, Store, Plus, Minus, Zap,
    LayoutDashboard, History, Users, LogOut, Printer, XCircle, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const POSStoreFront = () => {
    const { sellerId } = useParams();
    const navigate = useNavigate();
    const [seller, setSeller] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (sellerId) {
            fetchStoreData();
        }
    }, [sellerId]);

    const fetchStoreData = async () => {
        setLoading(true);
        try {
            const [sellerRes, prodRes] = await Promise.all([
                axios.get(`${API}/sellers/${sellerId}`),
                axios.get(`${API}/marketplace/products?seller_id=${sellerId}&service=pos`)
            ]);
            setSeller(sellerRes.data);
            setProducts(prodRes.data.filter(p => p.is_active));
        } catch (error) {
            toast.error("Magazen sa a pa disponib.");
            navigate("/pos");
        } finally {
            setLoading(false);
        }
    };

    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item => item.id === product.id ? { ...item, qty: item.qty + 1 } : item);
            }
            return [...prev, { ...product, qty: 1 }];
        });
        toast.success(`${product.title} ajoute`);
    };

    const updateQty = (productId, delta) => {
        setCart(prev => prev.map(item => {
            if (item.id === productId) {
                const newQty = Math.max(1, item.qty + delta);
                return { ...item, qty: newQty };
            }
            return item;
        }));
    };

    const removeFromCart = (productId) => {
        setCart(prev => prev.filter(item => item.id !== productId));
    };

    const total = cart.reduce((sum, item) => sum + (item.price_htg * item.qty), 0);
    const itemsCount = cart.reduce((sum, item) => sum + item.qty, 0);

    const handleCheckout = async () => {
        if (cart.length === 0) return toast.error("Panye a vid!");
        const token = localStorage.getItem("buyer_token");
        const buyerPhone = localStorage.getItem("buyer_phone");

        if (!token || !buyerPhone) {
            toast.error("Tanpri konekte pou w ka achte.");
            navigate("/linkamazon");
            return;
        }

        try {
            toast.info("Inisyalize peman MonCash...");
            const buyerData = JSON.parse(localStorage.getItem("buyer_user") || "{}");
            const subtotal = cart.reduce((acc, item) => acc + (item.price_htg * item.qty), 0);
            
            const orderRes = await axios.post(`${API}/marketplace/orders`, {
                buyer_name: buyerData.first_name ? `${buyerData.first_name} ${buyerData.last_name || ''}` : "Kliyan POS",
                buyer_phone: buyerPhone,
                delivery_address: "pickup",
                location: null,
                seller_id: cart[0]?.seller_id,
                items: cart.map(item => ({
                    product_id: item.id,
                    product_name: item.title || item.name,
                    price: Number(item.price_htg),
                    quantity: item.qty
                })),
                total_htg: subtotal,
                payment_method: "moncash",
                delivery_method: "pickup",
                delivery_fee: 0,
                recipient_name: null,
                recipient_phone: null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (orderRes.data.success) {
                const orderId = orderRes.data.order_id;
                const payRes = await axios.post(`${API}/marketplace/orders/${orderId}/pay-moncash`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (payRes.data.redirect_url) {
                    window.location.href = payRes.data.redirect_url;
                } else {
                    toast.error("Ere nan komanse peman an.");
                }
            } else {
                toast.error(orderRes.data.message || "Ere nan komande a.");
            }
        } catch (error) {
            console.error("Checkout Error:", error);
            const errMsg = error.response?.data?.message || error.response?.data?.detail || "Ere pandan peman an.";
            toast.error(errMsg);
        }
    };;

    const [mobileTab, setMobileTab] = useState("products"); // products, cart

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0F172A]"><Zap className="w-12 h-12 text-[#FF9900] animate-spin" /></div>;

    const filteredProducts = products.filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <div className="h-[100dvh] flex flex-col bg-[#0F172A] text-white font-sans overflow-hidden">
            {/* Compact Header for Mobile, Full for Desktop */}
            <header className="h-14 lg:h-16 bg-[#171E2E] border-b border-white/5 flex items-center px-4 lg:px-6 gap-4 justify-between shrink-0">
                <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-7 h-7 lg:w-8 lg:h-8 bg-[#FF9900] rounded-lg flex items-center justify-center text-black">
                        <Store className="w-4 h-4 lg:w-5 lg:h-5" />
                    </div>
                    <span className="text-sm lg:text-xl font-black italic tracking-tighter uppercase text-[#FF9900] truncate max-w-[150px] lg:max-w-none">
                        {seller?.store_name || "POS SYSTEM"}
                    </span>
                </div>

                <div className="hidden lg:flex flex-1 max-w-2xl relative mx-8">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <Input 
                        placeholder="Chèche yon pwodwi..." 
                        className="h-10 bg-[#0F172A] border-none rounded-md pl-12 text-sm text-gray-300 placeholder:text-gray-600 focus-visible:ring-1 focus-visible:ring-[#FF9900]/50"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="flex items-center gap-3 lg:gap-6">
                    <div className="hidden sm:flex items-center gap-2 text-[9px] font-bold text-gray-400">
                        <Zap className="w-3 h-3 text-[#FF9900]" />
                        <span>ONLINE</span>
                    </div>
                    <Button variant="ghost" size="sm" className="lg:hidden h-8 w-8 p-0" onClick={() => setMobileTab(mobileTab === 'products' ? 'cart' : 'products')}>
                        {mobileTab === 'products' ? <ShoppingBag className="w-5 h-5 text-[#FF9900]" /> : <Plus className="w-5 h-5 text-[#FF9900]" />}
                    </Button>
                </div>
            </header>

            {/* Sticky Mobile Search - Only on Products Tab */}
            {mobileTab === 'products' && (
                <div className="lg:hidden px-4 py-2 bg-[#0F172A] border-b border-white/5 shrink-0">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                        <Input 
                            placeholder="Chèche..." 
                            className="h-9 bg-[#171E2E] border-none rounded-lg pl-9 text-[10px] text-gray-300"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            )}

            {/* Mobile Bottom Navigation (Tabs) */}
            <div className="lg:hidden flex bg-[#171E2E] border-t border-white/5 order-last shrink-0 pb-safe">
                <button 
                    onClick={() => setMobileTab("products")}
                    className={`flex-1 flex flex-col items-center justify-center py-4 transition-all ${mobileTab === 'products' ? 'text-[#FF9900] bg-white/5' : 'text-gray-500'}`}
                >
                    <Plus className="w-5 h-5 mb-1" />
                    <span className="text-[9px] font-black uppercase tracking-widest leading-none">Pwodwi</span>
                </button>
                <button 
                    onClick={() => setMobileTab("cart")}
                    className={`flex-1 flex flex-col items-center justify-center py-4 transition-all relative ${mobileTab === 'cart' ? 'text-[#FF9900] bg-white/5' : 'text-gray-500'}`}
                >
                    <ShoppingBag className="w-5 h-5 mb-1" />
                    <span className="text-[9px] font-black uppercase tracking-widest leading-none">Panye ({itemsCount})</span>
                    {itemsCount > 0 && (
                        <span className="absolute top-2 right-[38%] w-4 h-4 bg-red-600 text-white text-[8px] rounded-full flex items-center justify-center font-bold border-2 border-[#171E2E]">
                            {itemsCount}
                        </span>
                    )}
                </button>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">
                {/* Left Sidebar - Desktop Only */}
                <aside className="hidden lg:flex w-56 bg-[#171E2E] border-r border-white/5 flex-col shrink-0">
                    <div className="p-4 space-y-2">
                        <button className="w-full flex items-center gap-3 px-4 py-3 rounded-md bg-[#FF9900]/10 text-[#FF9900] transition-colors text-[10px] font-bold uppercase tracking-widest">
                            <Plus className="w-4 h-4" /> TOUT PWODWI
                        </button>
                        <button className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-gray-400 hover:bg-[#0F172A] hover:text-[#FF9900] transition-colors text-[10px] font-bold uppercase tracking-widest">
                            <ShoppingBag className="w-4 h-4" /> PANYE MWEN
                        </button>
                    </div>
                    <div className="mt-auto p-4 border-t border-white/5">
                        <button 
                            onClick={() => navigate("/pos")}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-gray-400 hover:bg-white/5 transition-colors text-[10px] font-bold uppercase tracking-widest"
                        >
                            <LogOut className="w-4 h-4" /> SOTI NAN MAGAZEN
                        </button>
                    </div>
                </aside>

                {/* Main POS Interface (Cart List) */}
                <main className={`flex-1 flex flex-col min-w-0 bg-[#0A0F1D] ${mobileTab === 'cart' ? 'flex' : 'hidden lg:flex'}`}>
                    {/* Items Table Header - Desktop Only */}
                    <div className="hidden lg:grid grid-cols-12 px-6 py-4 border-b border-white/5 bg-[#171E2E] text-gray-500 text-[9px] font-black uppercase tracking-widest">
                        <div className="col-span-1">Atik #</div>
                        <div className="col-span-5">Non Pwodwi</div>
                        <div className="col-span-2 text-right">Pri</div>
                        <div className="col-span-2 text-center">Kantite</div>
                        <div className="col-span-2 text-right px-4">Total</div>
                    </div>

                    {/* Cart Items List */}
                    <div className="flex-1 overflow-y-auto no-scrollbar p-0">
                        {cart.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-700/30 p-10 text-center">
                                <ShoppingBag className="w-16 lg:w-24 h-16 lg:h-24 mb-6 animate-pulse" />
                                <h3 className="text-lg lg:text-2xl font-black uppercase italic tracking-widest text-gray-800">Panye vid</h3>
                                <p className="text-[9px] lg:text-[10px] mt-2 font-bold uppercase tracking-widest text-gray-600">
                                    {mobileTab === 'cart' ? 'Chwazi "Pwodwi" pou w kòmanse' : 'Klike sou pwodwi yo bò dwat la pou w kòmanse achte'}
                                </p>
                            </div>
                        ) : (
                            cart.map(item => (
                                <div key={item.id} className="flex flex-col lg:grid lg:grid-cols-12 px-4 lg:px-6 py-4 border-b border-white/5 hover:bg-white/5 transition-colors group gap-4 lg:gap-0">
                                    {/* Item Info */}
                                    <div className="lg:col-span-1 hidden lg:block text-[10px] font-mono text-gray-500">#{item.id.slice(-4)}</div>
                                    <div className="lg:col-span-5 flex flex-col justify-center">
                                        <div className="text-xs lg:text-sm font-black text-gray-200 uppercase tracking-tight italic">{item.title}</div>
                                        <div className="lg:hidden text-[10px] font-bold text-gray-500 mt-1">{item.price_htg.toLocaleString()} HTG pa inite</div>
                                    </div>

                                    {/* Desktop Price */}
                                    <div className="hidden lg:flex lg:col-span-2 items-center justify-end text-xs font-bold text-gray-400">{item.price_htg.toLocaleString()} HTG</div>
                                    
                                    {/* Quantity Controls */}
                                    <div className="lg:col-span-2 flex items-center justify-between lg:justify-center gap-4 bg-white/5 lg:bg-transparent px-4 py-2 lg:p-0 rounded-xl lg:rounded-none">
                                        <span className="lg:hidden text-[10px] font-black uppercase text-gray-500 tracking-widest">Kantite</span>
                                        <div className="flex items-center gap-4">
                                            <button onClick={() => updateQty(item.id, -1)} className="w-8 lg:w-6 h-8 lg:h-6 rounded-full bg-white/10 lg:bg-white/5 hover:bg-red-500 hover:text-white flex items-center justify-center text-white lg:text-gray-500 transition-colors"><Minus className="w-3 h-3"/></button>
                                            <span className="text-sm font-black min-w-[20px] text-center">{item.qty}</span>
                                            <button onClick={() => updateQty(item.id, 1)} className="w-8 lg:w-6 h-8 lg:h-6 rounded-full bg-white/10 lg:bg-white/5 hover:bg-green-500 hover:text-white flex items-center justify-center text-white lg:text-gray-500 transition-colors"><Plus className="w-3 h-3"/></button>
                                        </div>
                                    </div>

                                    {/* Total Total & Remove */}
                                    <div className="lg:col-span-2 flex items-center justify-between lg:justify-end gap-3 lg:px-4">
                                        <span className="lg:hidden text-[10px] font-black uppercase text-gray-500 tracking-widest">Total</span>
                                        <div className="flex items-center gap-4">
                                            <div className="text-sm lg:text-xs font-black text-[#10B981] italic tracking-tighter">{(item.price_htg * item.qty).toLocaleString()} <span className="text-[10px] lg:hidden">HTG</span></div>
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); removeFromCart(item.id); }}
                                                className="w-8 h-8 lg:w-7 lg:h-7 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all"
                                                title="Retire pwodwi sa"
                                            >
                                                <Trash2 className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Mobile Checkout Footer - Overlayed at bottom of cart tab */}
                    <footer className={`h-auto lg:h-24 bg-[#171E2E] border-t-2 border-[#1E293B] flex flex-col lg:flex-row items-center p-4 lg:px-10 gap-4 lg:gap-10 shrink-0 ${mobileTab === 'cart' ? 'flex' : 'hidden lg:flex'}`}>
                        <div className="flex items-center justify-between w-full lg:w-auto lg:gap-10">
                            <div className="flex flex-col">
                                <span className="text-[8px] lg:text-[9px] font-black text-gray-500 uppercase tracking-widest leading-none mb-1">Kantite</span>
                                <span className="text-lg lg:text-2xl font-black italic tracking-tighter text-gray-300">{itemsCount} inite</span>
                            </div>

                            <div className="flex flex-col text-right">
                                <span className="text-[8px] lg:text-[9px] font-black text-gray-500 uppercase tracking-widest leading-none mb-1">Total Net</span>
                                <span className="text-xl lg:text-3xl font-black italic tracking-tighter text-[#10B981]">{total.toLocaleString()} <span className="text-[10px]">HTG</span></span>
                            </div>
                        </div>

                        <div className="hidden lg:flex flex-1"></div>

                        <div className="grid grid-cols-2 lg:flex items-center gap-3 w-full lg:w-auto">
                            <Button 
                                variant="outline"
                                onClick={() => setCart([])}
                                className="h-12 lg:h-12 px-4 lg:px-6 border-red-900/10 bg-red-950/10 hover:bg-red-900 text-red-500 hover:text-white text-[9px] font-black uppercase tracking-widest rounded-xl transition-all"
                            >
                                VIDID
                            </Button>
                            <Button 
                                onClick={handleCheckout}
                                disabled={cart.length === 0}
                                className="h-12 lg:h-14 px-6 lg:px-10 bg-[#FF9900] hover:bg-[#E88B00] text-black text-[10px] lg:text-[11px] font-black uppercase tracking-widest rounded-xl shadow-2xl flex items-center justify-center gap-2 lg:gap-3 transition-all active:scale-95 disabled:opacity-50"
                            >
                                <CreditCard className="w-4 h-4 lg:w-5 lg:h-5" /> PEYE KOUNYE A
                            </Button>
                        </div>
                    </footer>
                </main>

                {/* Right Items Grid - Optimized for Mobile Catalog View */}
                <aside className={`flex-1 lg:w-80 lg:flex-none bg-[#171E2E] border-l border-white/5 p-4 lg:p-6 overflow-y-auto no-scrollbar shrink-0 ${mobileTab === 'products' ? 'block' : 'hidden lg:block'}`}>
                    <h3 className="hidden lg:flex text-[10px] font-black text-gray-500 uppercase tracking-widest mb-6 items-center gap-2">
                         <Plus className="w-4 h-4 text-[#FF9900]" /> CHWAZI PWODWI OU YO
                    </h3>
                    
                    {filteredProducts.length === 0 ? (
                        <div className="py-20 text-center opacity-30">
                            <Package className="w-12 h-12 mx-auto mb-4" />
                            <p className="text-[10px] font-bold uppercase tracking-widest italic">Pa gen okenn pwodwi</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-3 pb-24 lg:pb-0">
                            {filteredProducts.map(product => (
                                <button
                                    key={product.id}
                                    onClick={() => addToCart(product)}
                                    className="group flex flex-col p-2.5 bg-[#0F172A] rounded-2xl border border-white/5 hover:border-[#FF9900]/50 transition-all text-left relative overflow-hidden"
                                >
                                    <div className="aspect-square rounded-xl overflow-hidden bg-gray-900 mb-2 relative">
                                        {product.images?.[0] ? (
                                            <img src={product.images[0]} alt="" className="w-full h-full object-cover lg:group-hover:scale-110 transition-transform duration-500" />
                                        ) : (
                                            <Package className="w-6 h-6 text-gray-700 m-auto mt-4" />
                                        )}
                                        <div className="absolute inset-0 bg-black/40 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                            <Plus className="w-6 h-6 text-[#FF9900]" />
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-black text-gray-200 truncate uppercase mt-1 leading-tight italic tracking-tighter">{product.title}</span>
                                    <div className="flex items-center justify-between mt-1">
                                        <span className="text-[11px] font-black text-[#FF9900] italic tracking-tighter">{product.price_htg.toLocaleString()} HTG</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </aside>
            </div>
        </div>
    );
};

